'use client';

import React, { useCallback } from 'react';
import { Divider, Flex, Tab, TabList, TabPanel, TabPanels, Tabs } from '@mezzoforte/forge';
import { useQueryStates } from 'nuqs';
import { Box, Text } from '@chakra-ui/react';
import { useListFilterParams } from '@/hooks/useListFilterParams';
import { EntryListFilterForm, EntryListFilterValues } from '@/components/ListPage/EntryListFilterForm';
import { AddSavedSearch } from '@/components/ListPage/AddSavedSearch';
import {
  EntryListFilterType,
  EntryListQuery,
  ListFilterOdometer,
  paramsToQuery,
  querySortToSortTab,
  searchParamsParsers,
  SellerType,
} from '@/features/EntryList/searchParams';
import { EntryListAPIResponse } from '@/types/ApiResponse';

function shouldShowSavedSearchButton(query: EntryListQuery) {
  return query.filter?.searchTerm !== undefined || query.filter?.company !== undefined;
}

interface EntryListFilterProps {
  categoryId?: number;
  baseQuery?: Partial<EntryListQuery>;
  currentPage: number;
  totalItemCount: number;
  manufacturers?: EntryListAPIResponse['aggregations']['manufacturers'];
}

export function EntryListFilter({
  categoryId,
  baseQuery,
  currentPage,
  totalItemCount,
  manufacturers,
}: EntryListFilterProps) {
  const [queryFilter, setQueryFilter] = useQueryStates(searchParamsParsers, { shallow: false });

  const { query, setQueryPage, onSortChange } = useListFilterParams({
    ...baseQuery?.filter,
    ...paramsToQuery(queryFilter).filter,
  });

  const onChangeFilter = useCallback(
    (values: EntryListFilterValues) => {
      const parseSelect = (v: string | undefined) => (v !== undefined && v !== '' ? v : null);
      const parseSelectAsNumber = (v: string | undefined) => (v !== undefined && v !== '' ? Number(v) : null);

      void setQueryFilter({
        region: values.region !== '' ? values.region : null,
        tyyppi: values.type !== EntryListFilterType.UsedAndNew ? values.type : null,
        met: values.soldForHighestBidder ? true : null,
        paattyneet: values.onlyEnded ? true : null,
        merkki: parseSelect(values.metadataManufacturer),
        vetotapa: parseSelectAsNumber(values.metadataDrive),
        km: parseSelect(values.metadataOdometer) as ListFilterOdometer,
        vaihteisto: parseSelectAsNumber(values.metadataTransmission),
        polttoaine: parseSelectAsNumber(values.metadataFuelType),
        vuosimalliMin: parseSelectAsNumber(values.metadataYearModelStart),
        vuosimalliMax: parseSelectAsNumber(values.metadataYearModelEnd),
        sellerType: values.sellerType !== SellerType.All ? values.sellerType : null,
      });
      void setQueryPage(1);
    },
    [setQueryFilter, setQueryPage]
  );

  return (
    <>
      <EntryListFilterForm
        categoryId={categoryId}
        companyId={baseQuery?.filter?.company}
        defaultValues={{
          soldForHighestBidder: query.filter.soldForHighestBidder,
          onlyEnded: query.filter.onlyEnded,
          region: query.filter.region,
          type: query.filter.type,
          metadataManufacturer: query.filter.metadataManufacturer?.toLowerCase(),
          metadataDrive: query.filter.metadataDrive?.toString(),
          metadataOdometer: query.filter.metadataOdometer,
          metadataTransmission: query.filter.metadataTransmission?.toString(),
          metadataFuelType: query.filter.metadataFuelType?.toString(),
          metadataYearModelStart: query.filter.metadataYearModelStart?.toString(),
          metadataYearModelEnd: query.filter.metadataYearModelEnd?.toString(),
          sellerType: query.filter.sellerType,
        }}
        onSubmit={onChangeFilter}
        manufacturers={manufacturers}
      />

      {shouldShowSavedSearchButton(query) && (
        <Box my={4}>
          <AddSavedSearch query={query} />
        </Box>
      )}

      <Divider my={5} />

      <Flex
        mb={5}
        direction={{ base: 'column', md: 'row-reverse' }}
        align={{ base: 'start', md: 'center' }}
        justify="space-between"
        gap={3}
      >
        {/* TODO: Rewrite tabs in Forge, Chakra's tabs' defaultIndex does not support SSR properly. */}
        <Tabs
          defaultIndex={Number(querySortToSortTab[query.sort] ?? 0)}
          onChange={onSortChange}
          sx={{
            '> div': {
              borderBottom: 'none',
            },
          }}
        >
          <TabList>
            <Tab>Päättyvät</Tab>
            <Tab>Kiinnostavimmat</Tab>
            <Tab>Uusimmat</Tab>
          </TabList>
          <TabPanels display="none">
            <TabPanel />
            <TabPanel />
            <TabPanel />
          </TabPanels>
        </Tabs>
        <Text>
          {totalItemCount} ilmoitusta, sivu {currentPage}
        </Text>
      </Flex>
    </>
  );
}
